import React, { useState, useEffect, useRef } from "react";
import { AudiencePicker, HeaderImageField, ImageUploadButton, audienceLabel } from "@/components/staff/EmailTools";
import { CampaignsPanel } from "@/components/staff/CampaignsPanel";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Plus, Loader2, Trash2, Edit2, Send, Eye, Clock, Users, BarChart3 } from "lucide-react";

const NewslettersPage = () => {
  const { token } = useAuth();
  const [newsletters, setNewsletters] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = { title: "", subject: "", content: "", segments: ["patients", "subscribers"], header_image_url: "", scheduled_for: "", status: "draft" };
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState("newsletters");
  const contentRef = useRef(null);
  const insertImage = (url) => {
    const el = contentRef.current;
    const pos = el ? el.selectionStart : form.content.length;
    const before = form.content.slice(0, pos), after = form.content.slice(pos);
    const snippet = `${before && !before.endsWith("\n") ? "\n" : ""}![image](${url})${after.startsWith("\n") ? "" : "\n"}`;
    setForm(f => ({ ...f, content: before + snippet + after }));
    toast.success("Image added to the email body.");
  };
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);
  const [stats, setStats] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...headers, "Content-Type": "application/json" };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [nRes, sRes] = await Promise.all([
        fetch(`${API}/staff/newsletters?limit=50`, { headers }),
        fetch(`${API}/staff/marketing-stats`, { headers }),
      ]);
      if (nRes.ok) { const d = await nRes.json(); setNewsletters(d.newsletters); setTotal(d.total); }
      if (sRes.ok) setStats(await sRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    if (!form.segments.length) { toast.error("Choose at least one audience"); return; }
    setSaving(true);
    try {
      const url = editing ? `${API}/staff/newsletters/${editing}` : `${API}/staff/newsletters`;
      const body = { ...form };
      if (body.scheduled_for && body.status === "draft") body.status = "scheduled";
      const res = await fetch(url, { method: editing ? "PUT" : "POST", headers: jsonHeaders, body: JSON.stringify(body) });
      if (res.ok) { toast.success(editing ? "Newsletter updated" : "Newsletter created"); setShowForm(false); setEditing(null); resetForm(); fetchAll(); }
      else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Connection error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this newsletter?")) return;
    try {
      const res = await fetch(`${API}/staff/newsletters/${id}`, { method: "DELETE", headers });
      if (res.ok) { toast.success("Deleted"); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const handleSend = async (id) => {
    if (!confirm("Send this newsletter now? This will email all recipients in the selected segment.")) return;
    setSending(id);
    try {
      const res = await fetch(`${API}/staff/newsletters/${id}/send`, { method: "POST", headers });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchAll(); }
      else { const e = await res.json(); toast.error(e.detail || "Send failed"); }
    } catch { toast.error("Connection error"); }
    finally { setSending(null); }
  };

  const handlePreview = async (id) => {
    try {
      const res = await fetch(`${API}/staff/newsletters/${id}/preview`, { method: "POST", headers });
      if (res.ok) { const d = await res.json(); toast.success(d.message); }
      else { const e = await res.json(); toast.error(e.detail || "Preview failed"); }
    } catch { toast.error("Connection error"); }
  };

  const resetForm = () => setForm(EMPTY);
  const legacySegments = (nl) => nl.segments?.length ? nl.segments : nl.segment === "all" || !nl.segment ? ["subscribers", "patients"] : [nl.segment === "website" ? "subscribers" : nl.segment];

  const statusBadge = (s) => {
    const map = { draft: "bg-stone-100 text-stone-600", scheduled: "bg-blue-50 text-blue-700", sent: "bg-green-50 text-green-700", sending: "bg-amber-50 text-amber-700" };
    return <Badge className={`${map[s] || map.draft} text-xs`}>{s}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="newsletters-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Newsletters</h1>
          <p className="mt-1 text-base text-stone-500">Create, schedule, and send email campaigns.</p>
        </div>
        {tab === "newsletters" && <Button onClick={() => { resetForm(); setEditing(null); setShowForm(true); }} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="create-newsletter-btn">
          <Plus className="w-4 h-4 mr-1" /> New Newsletter
        </Button>}
      </div>

      <div className="flex gap-1 border-b border-stone-200">
        {[["newsletters", "Newsletters"], ["campaigns", "Campaign links"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`nl-tab-${k}`} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-[#FF7A00] text-[#FF7A00]" : "border-transparent text-stone-500 hover:text-stone-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "campaigns" ? <CampaignsPanel token={token} /> : <>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat label="Total" value={stats.newsletters?.total || 0} icon={Mail} />
          <MiniStat label="Sent" value={stats.newsletters?.sent || 0} icon={Send} color="text-green-600" />
          <MiniStat label="Scheduled" value={stats.newsletters?.scheduled || 0} icon={Clock} color="text-blue-600" />
          <MiniStat label="Active Subscribers" value={stats.subscribers?.total_active || 0} icon={Users} color="text-[#FF7A00]" />
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="rounded-xl border-[#FF7A00]/30 p-5 space-y-4" data-testid="newsletter-form">
          <h3 className="font-bold text-stone-900">{editing ? "Edit newsletter" : "New newsletter"}</h3>
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Newsletter title" className="rounded-lg" data-testid="nl-title" />
          <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Email subject line" className="rounded-lg" data-testid="nl-subject" />
          <HeaderImageField value={form.header_image_url} onChange={v => setForm(f => ({ ...f, header_image_url: v }))} token={token} testid="nl-header" />
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-stone-600">Email body</label>
              <ImageUploadButton token={token} onUploaded={insertImage} label="Insert image" testid="nl-insert-image" />
            </div>
            <Textarea ref={contentRef} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Write your email. Use **bold** and [link text](https://...). Images are inserted where your cursor is." rows={10} className="rounded-lg font-mono text-[13px]" data-testid="nl-content" />
            <p className="text-[11px] text-stone-400">Tip: write {"{name}"} to greet each person by first name.</p>
          </div>
          <AudiencePicker value={form.segments} onChange={v => setForm(f => ({ ...f, segments: v }))} token={token} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-stone-600 mb-1 block">Schedule for (optional)</label>
              <Input type="datetime-local" value={form.scheduled_for} onChange={e => setForm({ ...form, scheduled_for: e.target.value })} className="rounded-lg" data-testid="nl-schedule" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="nl-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} {form.scheduled_for ? "Schedule" : "Save draft"}
            </Button>
            <Button variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Newsletter List */}
      {newsletters.length > 0 ? (
        <div className="space-y-2">
          {newsletters.map(nl => (
            <Card key={nl.newsletter_id} className="rounded-xl border-stone-200/60 p-4" data-testid={`nl-${nl.newsletter_id}`}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FFF7F0] flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-[#FF7A00]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-stone-900">{nl.title}</span>
                    {statusBadge(nl.status)}
                    {legacySegments(nl).map(sg => <Badge key={sg} variant="outline" className="text-xs">{audienceLabel(sg)}</Badge>)}
                    {nl.header_image_url && <Badge variant="outline" className="text-[10px] text-stone-400">header image</Badge>}
                  </div>
                  {nl.subject && <p className="text-xs text-stone-500">Subject: {nl.subject}</p>}
                  <p className="text-xs text-stone-400 mt-0.5">
                    {nl.status === "sent" ? `Sent ${nl.sent_at?.split("T")[0]} to ${nl.recipients_count || 0} (${nl.delivered_count || 0} delivered, ${nl.bounced_count || 0} bounced)` : `Created ${nl.created_at?.split("T")[0]}`}
                    {nl.scheduled_for && nl.status === "scheduled" && ` - Scheduled for ${nl.scheduled_for}`}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  {nl.status === "draft" && (
                    <>
                      <Button size="sm" variant="ghost" className="text-xs" onClick={() => handlePreview(nl.newsletter_id)} data-testid={`preview-${nl.newsletter_id}`}>
                        <Eye className="w-3 h-3 mr-1" /> Preview
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs rounded-lg text-[#FF7A00] border-[#FF7A00]/30" onClick={() => handleSend(nl.newsletter_id)} disabled={sending === nl.newsletter_id} data-testid={`send-${nl.newsletter_id}`}>
                        {sending === nl.newsletter_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Send
                      </Button>
                      <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setForm({ title: nl.title, subject: nl.subject || "", content: nl.content || "", segments: legacySegments(nl), header_image_url: nl.header_image_url || "", scheduled_for: nl.scheduled_for || "", status: nl.status }); setEditing(nl.newsletter_id); setShowForm(true); }}>
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-xs text-red-500" onClick={() => handleDelete(nl.newsletter_id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  {nl.status === "scheduled" && (
                    <Button size="sm" variant="ghost" className="text-xs text-red-500" onClick={() => handleDelete(nl.newsletter_id)}>
                      <Trash2 className="w-3 h-3" /> Cancel
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="rounded-xl border-stone-200/60 p-12 text-center">
          <Mail className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500">No newsletters yet. Create your first one.</p>
        </Card>
      )}
      </>}
    </div>
  );
};

const MiniStat = ({ label, value, icon: Icon, color = "text-stone-700" }) => (
  <Card className="rounded-xl border-stone-200/60 p-3 text-center">
    <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
    <p className={`text-lg font-bold ${color}`}>{value}</p>
    <p className="text-xs text-stone-400">{label}</p>
  </Card>
);

export default NewslettersPage;
